// AI Email Writing Assistant using Groq API (Free, Fast, Unlimited)
// Generates professional spam-free emails

// Get API key from .env file (fallback to localStorage)
const getGroqApiKey = () => {
  // First try environment variable - check both process.env and window
  const envKey = process.env.REACT_APP_GROQ_API_KEY;
  
  // Debug logging (remove in production)
  console.log('🔑 API Key Check:');
  console.log('Environment key exists:', !!envKey);
  console.log('Environment key length:', envKey ? envKey.length : 0);
  console.log('Environment key preview:', envKey ? envKey.substring(0, 10) + '...' : 'N/A');
  
  if (envKey && envKey !== 'gsk_your_free_groq_api_key_here' && envKey.startsWith('gsk_')) {
    console.log('✅ Using environment API key');
    return envKey;
  }
  
  // Fallback to localStorage (user can still set it in UI)
  const localKey = localStorage.getItem('groq_api_key');
  console.log('localStorage key exists:', !!localKey);
  
  if (localKey && localKey.startsWith('gsk_')) {
    console.log('✅ Using localStorage API key');
    return localKey;
  }
  
  console.error('❌ No valid API key found!');
  return '';
};

const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';

export async function generateEmailWithAI(prompt, options = {}) {
  const {
    tone = 'professional',
    length = 'medium',
    industry = 'logistics',
    includeGreeting = true,
    includeClosing = true,
    avoidSpam = true,
    mergeTags = [],
  } = options;
  
  const GROQ_API_KEY = getGroqApiKey();
  
  if (!GROQ_API_KEY) {
    return {
      success: false,
      error: 'No API key found. Please set REACT_APP_GROQ_API_KEY in .env file or configure in UI.',
      fallback: generateFallbackEmail(prompt, options),
    };
  }
  
  // Build merge tags list for AI
  const mergeTagsList = mergeTags.length > 0 
    ? mergeTags.map(tag => tag.tag).join(', ')
    : '{{name}}, {{company}}, {{mc_number}}, {{phone}}, {{email}}';
  
  const mergeTagsExamples = mergeTags.length > 0
    ? mergeTags.map(tag => `${tag.tag} - ${tag.label}`).join('\n')
    : '{{name}} - Recipient name\n{{company}} - Company name\n{{mc_number}} - MC authority number';

  const systemPrompt = `You are an ELITE email marketer specializing in the ${industry} industry. Your emails make receivers stop scrolling and take immediate action.

🎯 YOUR MISSION: Write emails that IMPRESS from the first word. Make them feel special, understood, and excited.

❌ FORBIDDEN - NEVER INCLUDE:
- "Here is a rewritten version..." "Here's a professional email..." "I've crafted..."
- ANY meta-commentary, explanations, or notes about the email
- Just write the ACTUAL email content ONLY

✅ PROFESSIONAL MARKETER RULES:
1. Output ONLY the email - as if an experienced marketer typed it
2. HOOK readers instantly - first sentence must grab attention
3. Use PSYCHOLOGY: curiosity, exclusivity, social proof, urgency (natural, not spammy)
4. Be conversational yet professional - like talking to a respected peer
5. Show you RESEARCHED them - use merge tags to personalize deeply
6. Create DESIRE before asking - paint the picture of success
7. Include PROOF: metrics, numbers, testimonials, case studies
8. ZERO spam words: FREE, GUARANTEED, CLICK, ACT NOW, LIMITED TIME, URGENT!!!, WINNER, BUY

📧 WINNING EMAIL STRUCTURE:
Subject: [Compelling, personalized subject that creates curiosity - use merge tags]

[Warm, personalized greeting with {{name}}]

[HOOK: Pattern interrupt or relevant insight about {{company}} or {{state}}]

[Bridge: Why this matters to THEM specifically]

[Value Stack with PROOF:]
• Specific benefit + metric (e.g., "15-20% revenue increase")
• Real example or case study result
• Social proof element (e.g., "Trusted by 500+ carriers in {{state}}")
• Unique differentiator using {{mc_number}} or {{equipment_type}}

[Handle objection or create urgency naturally]

[Clear CTA with low friction - use {{phone}} or {{email}}]

[Professional sign-off with credibility]

🎨 FORMATTING TIPS:
- Short paragraphs (1-3 lines)
- Strategic emojis (1-3 max): 👋 ✅ 🚛 📞 💼 🤝 📈 ⭐
- Bullet points for benefits
- BOLD for key numbers/benefits (use text indicators)
- Line breaks for breathing room

🏆 ALL AVAILABLE MERGE TAGS (Use as many as make sense - NO LIMIT):
${mergeTagsList}

Tag Details:
${mergeTagsExamples}

🧠 SMART TAG STRATEGY:
- NO LIMITS - Use ANY tags that strengthen personalization
- MORE personalization = MORE impressive = HIGHER response
- Cold email: {{name}}, {{company}}, {{mc_number}}, {{state}}, {{equipment_type}}
- Warm outreach: ALL relevant tags to show deep research
- Follow-up: {{dot_number}}, {{authority_status}}, specific details
- Service pitch: {{phone}}, {{email}}, {{address}} for credibility

💡 ELITE COPYWRITING TRAITS:
- Power words: proven, exclusive, specialized, premium, dedicated
- Specific numbers: "15-20% higher rates" not "better rates"
- Social proof: "500+ carriers" "24/7 support" "15 years"
- Questions that engage: "What if you could..."
- Urgency without spam: "Limited onboarding slots" not "ACT NOW!!!"
- Personality: contractions, casual confidence, enthusiasm
- Personal touches
- Enthusiasm without being salesy

TONE: ${tone} but always genuine and conversational
LENGTH: ${length} (short=50-100 words, medium=100-200 words, long=200-400 words)

${includeGreeting ? 'START with a warm greeting using {{name}}' : ''}
${includeClosing ? 'END with a natural sign-off with sender info' : ''}

Remember: You're a human reaching out to another human. Write like you're sending this from your own inbox.`;


  const userPrompt = `Write an email about: ${prompt}

Context: ${tone} tone, ${length} length, ${industry} industry
ALL Available Tags: ${mergeTagsList}

🎯 WRITE AN IMPRESSIVE, PROFESSIONAL EMAIL:
1. Output ONLY email content - no meta-text
2. Start with Subject line
3. Sound like an experienced marketer, not AI
4. Use merge tags intelligently - NO LIMIT on how many (use all relevant ones)
5. Add strategic emojis (1-3) for warmth
6. Include PROOF and METRICS to impress
7. NO spam words

📱 HTML FORMAT FOR PROFESSIONAL DISPLAY:
- <p> for paragraphs (automatically adds spacing)
- <strong> for headings and important text
- <ul> and <li> for bullet lists
- <br> for line breaks within paragraphs
- <em> for emphasis/italics if needed

✨ PROFESSIONAL EMAIL TEMPLATE:
<p><strong>Subject: [Personalized subject with {{company}} or {{name}}]</strong></p>

<p>Hi {{name}} 👋</p>

<p>[Hook: Mention {{company}} or {{state}} - show you researched them]</p>

<p>[Bridge: Why this matters to carriers in {{state}} with {{equipment_type}}]</p>

<p><strong>What You Get:</strong></p>
<ul>
<li><strong>Benefit 1:</strong> Specific result (e.g., "15-20% higher rates") 📈</li>
<li><strong>Benefit 2:</strong> Unique feature with proof (e.g., "24/7 support - trusted by 500+ carriers")</li>
<li><strong>Benefit 3:</strong> Credibility using {{mc_number}} or {{authority_status}}</li>
</ul>

<p>[Natural transition - handle objection or create desire]</p>

<p><strong>Ready to explore?</strong> Give me a call at {{phone}} or reply to {{email}}</p>

<p>Best regards,<br><strong>{{sender_name}}</strong><br>{{company_name}}<br>{{phone}}</p>

🚀 NOW WRITE THE IMPRESSIVE, HTML-FORMATTED EMAIL:`;


  try {
    console.log('🤖 Starting AI email generation...');
    console.log('Using API key:', GROQ_API_KEY.substring(0, 10) + '...');
    console.log('Prompt length:', prompt.length);
    console.log('System prompt length:', systemPrompt.length);
    
    const requestBody = {
      model: 'llama-3.3-70b-versatile', // Updated to current Groq model
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.7,
      max_tokens: 1024,
      top_p: 1,
      stream: false,
    };
    
    console.log('Request body:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch(GROQ_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      
      let errorMessage = `API Error (${response.status}): ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText);
        console.error('Parsed error data:', errorData);
        
        if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = JSON.stringify(errorData.error);
        }
      } catch (e) {
        console.error('Could not parse error response:', e);
        errorMessage = `${errorMessage}\n\nRaw response: ${errorText}`;
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('✅ AI Response received');
    
    const generatedEmail = data.choices?.[0]?.message?.content || '';
    
    if (!generatedEmail) {
      console.error('Empty email in response:', data);
      throw new Error('AI returned empty response');
    }

    console.log('✅ Email generated successfully, length:', generatedEmail.length);
    return {
      success: true,
      email: generatedEmail,
      model: 'Llama 3.3 70B (Groq)',
    };
  } catch (error) {
    console.error('❌ AI Email Generation Error:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    return {
      success: false,
      error: error.message || 'Unknown error occurred',
      fallback: generateFallbackEmail(prompt, options),
    };
  }
}

export async function rewriteToAvoidSpam(originalText, spamWords = []) {
  const GROQ_API_KEY = getGroqApiKey();
  
  // Ensure originalText is a string
  const textToRewrite = typeof originalText === 'string' ? originalText : String(originalText || '');
  
  console.log('🛡️ rewriteToAvoidSpam called');
  console.log('📝 Input text length:', textToRewrite.length);
  console.log('📝 Input text type:', typeof textToRewrite);
  console.log('🚫 Spam words count:', spamWords.length);
  
  if (!textToRewrite.trim()) {
    console.warn('⚠️ Empty text provided to rewriteToAvoidSpam');
    return {
      success: false,
      error: 'No text provided to rewrite',
      email: textToRewrite,
    };
  }
  
  if (!GROQ_API_KEY) {
    console.error('❌ No API key found');
    return {
      success: false,
      error: 'No API key found. Please set REACT_APP_GROQ_API_KEY in .env file or configure in UI.',
      email: textToRewrite,
    };
  }
  
  const spamWordsList = Array.isArray(spamWords) && spamWords.length > 0
    ? spamWords.map(w => typeof w === 'object' ? w.word : w).filter(Boolean).join(', ')
    : 'free, guaranteed, click here, urgent, act now, limited time, winner, cash, money';
  
  console.log('🚫 Spam words to replace:', spamWordsList);
  
  const systemPrompt = `You are an ELITE email marketer who rewrites emails to be spam-free AND impressive.

❌ FORBIDDEN - NEVER include:
❌ "Here is a rewritten version..." "Here's the spam-free version..." "I've rewritten..."
❌ ANY explanations or meta-text

🎯 YOUR JOB: Output the cleaned email with professional HTML formatting.

SPAM WORDS TO REMOVE/REPLACE: ${spamWordsList}

✅ RULES:
1. Keep exact same meaning and intent
2. Replace spam words with POWERFUL professional alternatives
3. Enhance HTML formatting for visual impact
4. Use <p>, <strong>, <ul>, <li>, <br>, <em> tags
5. Add proper spacing and professional structure
6. Make it MORE impressive while being spam-free
7. Output ONLY the HTML email - no commentary

💡 REPLACEMENT EXAMPLES:
- "FREE" → "Complimentary" or "No cost"
- "GUARANTEED" → "Proven" or "Backed by results"
- "CLICK HERE" → "View details" or "Learn more"
- "URGENT!!!" → "Time-sensitive opportunity"`;

  const userPrompt = `Remove spam words and enhance with professional HTML formatting:

${textToRewrite}

✨ OUTPUT FORMAT (impressive professional design):
<p><strong>Subject: [Enhanced subject]</strong></p>
<p>Greeting...</p>
<p>Enhanced opening...</p>
<p><strong>Key Points:</strong></p>
<ul>
<li><strong>Point 1:</strong> With details</li>
<li><strong>Point 2:</strong> With proof</li>
</ul>
<p>Professional closing...</p>
<p>Best regards,<br><strong>Name</strong></p>

🚀 Make it spam-free AND impressive:`;

  try {
    console.log('🚀 Sending request to Groq API...');
    
    const response = await fetch(GROQ_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'llama-3.3-70b-versatile',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1024,
      }),
    });

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error:', errorText);
      throw new Error(`Groq API error: ${response.statusText}`);
    }

    const data = await response.json();
    const rewrittenEmail = data.choices?.[0]?.message?.content || '';
    
    console.log('✅ Rewritten email length:', rewrittenEmail.length);
    console.log('✅ Rewritten preview:', rewrittenEmail.substring(0, 200));

    return {
      success: true,
      email: rewrittenEmail,
    };
  } catch (error) {
    console.error('❌ AI Rewrite Error:', error);
    return {
      success: false,
      error: error.message,
      email: textToRewrite, // Return original text on error
    };
  }
}

export async function improveSentence(sentence, issue) {
  const GROQ_API_KEY = getGroqApiKey();
  
  if (!GROQ_API_KEY) {
    return sentence; // Return original if no key
  }
  
  const prompt = `Fix this sentence issue: "${issue}"

Original: ${sentence}

Output ONLY the improved sentence. No explanations.`;

  try {
    const response = await fetch(GROQ_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'llama-3.3-70b-versatile',
        messages: [
          { role: 'user', content: prompt },
        ],
        temperature: 0.6,
        max_tokens: 256,
      }),
    });

    const data = await response.json();
    return data.choices[0]?.message?.content || sentence;
  } catch (error) {
    console.error('AI Improve Error:', error);
    return sentence;
  }
}

function generateFallbackEmail(prompt, options) {
  const { tone, includeGreeting, includeClosing } = options;
  
  let email = '';
  
  if (includeGreeting) {
    email += 'Hi {{name}},\n\n';
  }
  
  email += `I wanted to reach out regarding ${prompt}.\n\n`;
  email += `Our team specializes in providing professional solutions for trucking and logistics companies. `;
  email += `We've helped many carriers like {{company}} streamline their operations.\n\n`;
  email += `Would you be interested in learning more about how we can support your business?\n\n`;
  
  if (includeClosing) {
    email += 'Best regards,\n';
    email += '{{sender_name}}\n';
    email += '{{sender_company}}';
  }
  
  return email;
}

export const EMAIL_TEMPLATES_SPAM_FREE = {
  loadboard: {
    subject: 'Freight Opportunity for {{company}}',
    body: `Hi {{name}},

I hope this message finds you well. I noticed your company operates in the {{state}} region and wanted to connect.

We have consistent freight opportunities that might align with your fleet's capabilities. Our partners value reliability and professional service.

Would you be interested in discussing potential collaboration?

Best regards,
{{sender_name}}
{{sender_company}}
{{sender_phone}}`,
  },
  
  dispatching: {
    subject: 'Professional Truck Dispatching Services - {{company}}',
    body: `Hello {{name}},

I hope you're having a great day. I wanted to introduce our truck dispatching services that help owner-operators and small fleets maximize their revenue.

Our dispatching team provides:
• 24/7 load searching and booking
• Rate negotiation with brokers
• Back-office support and documentation
• Fuel optimization and route planning

We work with carriers like {{company}} to keep trucks moving and earning. Our average clients see 15-20% revenue increase in the first 90 days.

Would you be open to a brief conversation about how we can support your operations?

Best regards,
{{sender_name}}
{{sender_company}}
{{sender_phone}}`,
  },
  
  mclease: {
    subject: 'MC Authority Lease Options - {{company}}',
    body: `Hi {{name}},

I wanted to reach out regarding MC authority lease opportunities for {{company}}.

Our MC lease program offers:
• Immediate access to established MC authority
• Insurance and compliance support included
• Flexible lease terms (weekly/monthly)
• Dedicated support team
• Fast approval process (24-48 hours)

This solution helps carriers start hauling quickly without the 3-4 week wait for new authority. Many of our partners are earning within days of signing up.

I'd be happy to discuss how this could benefit your operation. Are you available for a quick call this week?

Best regards,
{{sender_name}}
{{sender_title}}
{{sender_company}}`,
  },
  
  loads: {
    subject: 'Premium Load Opportunities - {{company}}',
    body: `Hello {{name}},

I hope this finds you well. I'm reaching out because we have premium load opportunities available for qualified carriers in your area.

Current available loads:
• Consistent weekly freight ({{state}} region)
• Competitive rates with quick pay options
• Direct shipper relationships
• Variety of equipment types

We work with professional carriers like {{company}} who value reliability and on-time delivery. Our loads are vetted and come from established shippers.

Would you be interested in receiving our weekly load board updates?

Let me know if you'd like more details.

Best,
{{sender_name}}
{{sender_company}}
{{sender_email}}`,
  },
  
  partnership: {
    subject: 'Partnership Inquiry - {{company}}',
    body: `Dear {{name}},

I'm reaching out to explore a potential partnership between our companies. We work with reputable carriers in the {{industry}} sector.

{{company}} caught my attention due to your track record and service quality. I believe there may be mutual benefits worth discussing.

Would you have time for a brief call this week?

Sincerely,
{{sender_name}}
{{sender_title}}
{{sender_company}}`,
  },
  
  followup: {
    subject: 'Following Up - {{company}}',
    body: `Hi {{name}},

I wanted to follow up on my previous message regarding potential opportunities for {{company}}.

I understand you're busy, so I'll keep this brief. If you're interested in learning more about our services, I'm happy to provide details at your convenience.

Thanks for your time.

Best,
{{sender_name}}`,
  },
};

export function getAPIKeyInstructions() {
  // Check if API key is set in .env
  const envKey = process.env.REACT_APP_GROQ_API_KEY;
  const hasEnvKey = envKey && envKey !== 'gsk_your_free_groq_api_key_here';
  
  return {
    title: 'Get Your Free Groq API Key',
    envConfigured: hasEnvKey,
    envKeyPreview: hasEnvKey ? `${envKey.substring(0, 10)}...` : null,
    steps: [
      '1. Visit https://console.groq.com',
      '2. Sign up for a free account (no credit card required)',
      '3. Go to API Keys section',
      '4. Create a new API key',
      hasEnvKey 
        ? '5. Replace REACT_APP_GROQ_API_KEY in .env file' 
        : '5. Copy and paste it in .env file as REACT_APP_GROQ_API_KEY',
    ],
    features: [
      '✓ Completely FREE',
      '✓ Fast AI responses (< 1 second)',
      '✓ No rate limits for reasonable use',
      '✓ Spam-free email generation',
      '✓ Professional writing assistance',
    ],
  };
}
